import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {adminLearnerStateApi, getErrorMessage} from '../services/api';
import './AdminLearnerState.css';

const PAGE_SIZE = 25;
const number = (value) => Number.isFinite(Number(value)) && value !== null && value !== undefined
    ? Number(value).toLocaleString('fa-IR') : '—';
const percent = (value, status) => {
    if (value === null || value === undefined || status === 'unassessed') return 'هنوز ارزیابی نشده';
    const ratio = Number(value);
    return Number.isFinite(ratio) && ratio >= 0 && ratio <= 1
        ? `${Math.round(ratio * 100).toLocaleString('fa-IR')}٪` : 'نامشخص';
};
const dateText = (value) => {
    if (!value) return 'شاهدی ثبت نشده';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'زمان نامشخص'
        : new Intl.DateTimeFormat('fa-IR', {dateStyle: 'medium'}).format(date);
};
const reasonNames = {
    insufficient_evidence: 'شواهد کافی برای مقایسهٔ ۳۰ روزه نیست',
    unknown_resource_version: 'نسخهٔ تاریخی منبع نامشخص است',
    resource_version_changed: 'نسخهٔ منبع تغییر کرده است',
    algorithm_version_changed: 'نسخهٔ الگوریتم تغییر کرده است',
};
const learnerPath = (row, courseId) => `/learner-state/${encodeURIComponent(row.learner_id)}?course=${encodeURIComponent(courseId)}`;
const detailPath = (row, courseId) => row.graph_id != null
    ? `/learner-state/${encodeURIComponent(row.learner_id)}/graphs/${encodeURIComponent(row.graph_id)}?course=${encodeURIComponent(courseId)}`
    : `/learner-state/${encodeURIComponent(row.learner_id)}/resources/${encodeURIComponent(row.resource_id)}?course=${encodeURIComponent(courseId)}`;

function Change({row}) {
    const delta = Number(row.mastery_delta_30d);
    if (row.mastery_delta_30d === null || row.mastery_delta_30d === undefined || !Number.isFinite(delta)) {
        return <span className="als-delta-muted" title={row.delta_reason || ''}>{reasonNames[row.delta_reason] || 'تغییر ۳۰ روزه قابل محاسبه نیست'}</span>;
    }
    const points = Math.round(Math.abs(delta) * 100).toLocaleString('fa-IR');
    return <span className={`als-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'same'}`}
        title={`از ${dateText(row.baseline_at)} تا ${dateText(row.current_at)}`}>
        {delta > 0 ? '+' : delta < 0 ? '−' : ''}{points} واحد درصد در ۳۰ روز
    </span>;
}

export default function CourseLearnerMonitor() {
    const {courseId} = useParams();
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [retry, setRetry] = useState(0);
    const [state, setState] = useState({data: null, loading: true, error: null});

    useEffect(() => {
        const timer = setTimeout(() => { setOffset(0); setSearch(query.trim()); }, 350);
        return () => clearTimeout(timer);
    }, [query]);
    useEffect(() => {
        let active = true;
        setState({data: null, loading: true, error: null});
        adminLearnerStateApi.courseSummary(courseId, {q: search, offset, limit: PAGE_SIZE})
            .then((data) => { if (active) setState({data, loading: false, error: null}); })
            .catch((error) => { if (active) setState({data: null, loading: false, error}); });
        return () => { active = false; };
    }, [courseId, search, offset, retry]);

    const {data, loading, error} = state;
    const rows = Array.isArray(data?.items) ? data.items : [];
    const total = Number(data?.total) || 0;
    return <main className="als-page" dir="rtl"><header className="als-header"><Link to="/organizations">مجموعه‌ها و دوره‌ها</Link>
        <span className="als-eyebrow">مدیریت سامانه · دورهٔ {number(courseId)}</span><h1>پایش یادگیرندگان دوره</h1>
        <p>هر ردیف، وضعیت یک یادگیرنده در یک منبع همین دوره است. تغییر ۳۰ روزه فقط برای نسخه‌های قابل‌مقایسه نمایش داده می‌شود.</p>
        <div className="als-actions"><button type="button" onClick={() => setRetry((value) => value + 1)}>به‌روزرسانی</button></div></header>
        <section className="als-card"><div className="als-section-head"><h2>یادگیرنده‌ها و منابع</h2><span>{loading ? 'در حال دریافت...' : `${number(total)} ردیف`}</span></div>
            <label className="als-course-search">جست‌وجوی نام، ایمیل یا شمارهٔ دانشجویی
                <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جست‌وجو در همین دوره"/></label>
            {loading && <p role="status">در حال دریافت وضعیت افراد دوره...</p>}
            {error && <div className="als-error" role="alert"><p>{error.status === 403 ? 'دسترسی به پایش این دوره ندارید.' : getErrorMessage(error)}</p>
                <button type="button" onClick={() => setRetry((value) => value + 1)}>تلاش دوباره</button></div>}
            {data && <><p className="als-hint">{number(data.scope?.resource_count)} منبع در دامنهٔ این گزارش · مرتب‌سازی بر اساس پاسخ سرور</p>
                {!rows.length && <p className="als-empty">{data.scope?.resource_count === 0 ? 'این دوره منبعی برای پایش ندارد.' : 'ردیفی مطابق جست‌وجو پیدا نشد.'}</p>}
                <div className="als-course-rows">{rows.map((row, index) => <article className="als-course-row" key={`${row.learner_id}:${row.resource_id}:${row.graph_id ?? 'no-graph'}:${index}`}>
                    <div><strong>{row.display_name || `یادگیرندهٔ ${row.learner_id}`}</strong><small>{row.student_number || 'شمارهٔ دانشجویی ثبت نشده'}</small></div>
                    <div><span className="als-eyebrow">منبع</span><b>{row.resource_title || 'منبع بدون عنوان'}</b><small>{row.graph_id == null ? 'بدون گراف مفهومی' : `${number(row.assessed_concepts)} از ${number(row.total_concepts)} مفهوم ارزیابی‌شده`}</small></div>
                    <div><span className="als-eyebrow">تسلط · اطمینان · پوشش</span><b>{percent(row.mastery, row.status)} · {percent(row.confidence)} · {percent(row.coverage)}</b><small>آخرین شاهد: {dateText(row.last_evidence_at)}</small></div>
                    <div><span className="als-eyebrow">تغییر تسلط</span><Change row={row}/></div>
                    <div className="als-course-links"><Link to={detailPath(row, courseId)}>جزئیات منبع</Link><Link to={learnerPath(row, courseId)}>پروندهٔ یادگیرنده</Link></div>
                </article>)}</div>
                {total > PAGE_SIZE && <nav className="als-pager" aria-label="صفحه‌بندی افراد دوره"><span>{number(offset + 1)} تا {number(Math.min(offset + PAGE_SIZE, total))} از {number(total)}</span>
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>قبلی</button>
                    <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>بعدی</button></nav>}</>}
        </section></main>;
}
